import { useAppDispatch, useAppSelector } from './hooks';
import { setPlayerDataLoad } from '../slices/playerDataLoad';
import { setNflGameDataLoad } from '../slices/nflGameDataLoad';
import { setLeagueDataLoad } from '../slices/leagueDataLoad';
import { setTradeDataLoad } from '../slices/tradeDataLoad';
import { setPlayerClaimDataLoad } from '../slices/playerClaimDataLoad';
import { setPlayerPoachDataLoad } from '../slices/playerPoachDataLoad';
import { setActionConfigDataLoad } from '../slices/actionConfigDataLoad';
import { configApi, leagueApi, leagueSeasonApi, leagueEventApi, tradeApi, teamApi, contractApi, playerClaimApi } from '../adapters/APIExporter';
import merge from 'deepmerge';
import { ActionConfig, League, LeagueSeason, TradeProposal, ContractStatus, TeamPlayerClaims, NflTeam, PlayerPoachInfo } from '../sdk/model';
import React, { useState } from 'react';

interface ActionConfigDataLoad {
  setIsException: (isException: boolean) => void,
  setLoadComplete: (isLoadComplete: boolean) => void,
}

export function useLoadActionConfigs(dataLoad: ActionConfigDataLoad): Map<string, ActionConfig> {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  var actionConfigs = useAppSelector((state) => state.actionConfigDataLoad);
  
  if (isLoading) {
    return new Map();
  }
  if (isLoaded) {
    return actionConfigs;
  }
  
  setIsLoading(true);
  
  configApi.getActionConfigs().then((resp) => {
    dispatch(setActionConfigDataLoad(resp.data));
    setIsLoading(false);
    setIsLoaded(true);
    dataLoad.setLoadComplete(true);
  }
  ).catch((err) => {
    setIsLoading(false); 
    setIsLoaded(true);
    dataLoad.setIsException(true);
    throw err;
  });

  return actionConfigs;
}

// TODO: We may want to think about how we want to handle reloading cached data. I want to avoid adding to much complexity to the hooks so maybe just a simple hook that will delete the cached data and then reload it.
interface LeagueDataLoad {
  userId: string | undefined,
  loadRoster: boolean,
  setIsException: (isException: boolean) => void,
  setLoadComplete: (isLoadComplete: boolean) => void,
}

export function useLoadLeagueDataQuickView(dataLoad: LeagueDataLoad) : League[] {
  // Navigator/Dispatcher
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  console.trace("Starting to Load League Data Quick View (loading: " + isLoading + ")");

  // State
  var leagues = useAppSelector((state) => state.leagueDataLoad);
  
  if (!dataLoad.userId) {
	  return [];
  }

  // Coupling the loading of the league data with the loading the data itself (i.e. check to see if the league exist to decide if we should call the backend)
  // Was causing some weirdness while loading the data. So we are going to decouple the two and keep all the loading information directly in the frontend.
  // Note: I think there are libraries for doing this, but that would be a major change since we are working so closely with Openapi Generator
  if (isLoading) {
    return [];
  }
  if (isLoaded) {
    return leagues;
  }

  setIsLoading(true);
  
  var res = leagueApi.getLeagues(true, dataLoad.userId).then((resp) => {
    dispatch(setLeagueDataLoad(resp.data));
    console.trace("Load League Data Quick View complete");
    setIsLoading(false);
    setIsLoaded(true);
    dataLoad.setLoadComplete(true);
  }
  ).catch((err) => {
    console.trace("Load League Data Quick View error");
    setIsLoading(false); 
    setIsLoaded(true);
    dataLoad.setIsException(true);
    throw err;
  });

  return leagues;

}

interface LoadLeagueSeasonData {
  leagueId: string | undefined,
  userId: String | undefined,
  loadBasicDraftData: boolean,
  loadFullDraftData: boolean,
  loadBasicAuctionData: boolean,
  loadFullAuctionData: boolean,
  loadContractData: boolean,
  loadRosterData: boolean,
  loadRosterFor: string[],
  loadWaivedContracts: boolean,
  loadScheduleData: boolean,
  isException: boolean,
  setIsException: (isException: boolean) => void,
  isLoadComplete: boolean,
  setLoadComplete: (isLoadComplete: boolean) => void,
  reloading: boolean,
  setReloading: (reloading: boolean) => void
}

// TODO: If this gets called a bunch (which it probably will) it will spam the backend with requests
export function useLoadLeagueData(dataLoad: LoadLeagueSeasonData) {
  // Navigator/Dispatcher
  const dispatch = useAppDispatch();
  // Stage 1 Load League
  const [isLoadingLeague, setIsLoadingLeague] = useState(false);
  const [isLoadedLeague, setIsLoadedLeague] = useState(false);
  // Stage 2 Load Season
  const [isLoadingSeason, setIsLoadingSeason] = useState(false);
  const [isLoadedSeason, setIsLoadedSeason] = useState(false);
  // Stage 3 (Optional) Load Full Draft
  const [isLoadingFullDraft, setIsLoadingFullDraft] = useState(false);
  const [isLoadedFullDraft, setIsLoadedFullDraft] = useState(false);
  // Stage 4 (Optional) Load Waived Contracts
  const [isLoadingWaivedContracts, setIsLoadingWaivedContracts] = useState(false);
  const [isLoadedWaivedContracts, setIsLoadedWaivedContracts] = useState(false);
  // Stage 5 (Optional) Load Schedule Data
  const [isLoadingScheduleData, setIsLoadingScheduleData] = useState(false);
  const [isLoadedScheduleData, setIsLoadedScheduleData] = useState(false);
  
  const [isLoadingRoster, setIsLoadingRoster] = useState(false);
  const [newTeam, setNewTeam] = useState(null);
  const [oldLoadRosterFor, setOldLoadRosterFor] = useState([]);
  const [oldLeagueId, setOldLeagueId] = useState(dataLoad.leagueId);

  let loadRosterFor = dataLoad.loadRosterFor || [];
  
  React.useEffect(() => {
    if (
        // If the loadRosterFor exist
        loadRosterFor 
          
        // And all the values in loadRosterFor are NOT covered by oldLoadRosterFor
        && !loadRosterFor.every((value) => oldLoadRosterFor.includes(value))
        
        // If the league id has changed
        || dataLoad.leagueId !== oldLeagueId) {
      setIsLoadingLeague(false);
      setIsLoadedLeague(false);
      setIsLoadingSeason(false);
      setIsLoadedSeason(false);
      setIsLoadingFullDraft(false);
      setIsLoadedFullDraft(false);
      setIsLoadingRoster(false);
      dataLoad.setLoadComplete(false);
      setOldLoadRosterFor(loadRosterFor);
      setOldLeagueId(dataLoad.leagueId);
    }
  }, [loadRosterFor, dataLoad.leagueId]);

  // Set State
  var leagues: League[] = useAppSelector((state) => state.leagueDataLoad);
  
  React.useEffect(() => {
      if (dataLoad.reloading) {
        setIsLoadingLeague(false);
        setIsLoadedLeague(false);
        setIsLoadingSeason(false);
        setIsLoadedSeason(false);
        setIsLoadingFullDraft(false);
        setIsLoadedFullDraft(false);
        setIsLoadingRoster(false);
        dataLoad.setLoadComplete(false);
        const newLeagues = leagues.filter(league => league.id !== dataLoad.leagueId);
        dispatch(setLeagueDataLoad(newLeagues));
      }
    }, [dataLoad.reloading]);
  // Find relevant league
  var league: League | undefined = findLeague(dataLoad.leagueId! , leagues);
  
  React.useEffect(() => {
    if (newTeam) {
      const newLeague = {...league};
      newLeague.teams = league!.teams.map(existingTeam => {
        if (existingTeam.id === newTeam.id) {
          newTeam.owners = existingTeam?.owners;
          return newTeam;
        }
        return existingTeam;
      });
      dispatch(setLeagueDataLoad(replaceLeague(newLeague, leagues)));
      setNewTeam(null);
    }
  }, [newTeam]);

  // If there is a data load exception return undefined
  if(dataLoad.isException) {
    return undefined;
  }

  // If the league is already loaded return it
  if(dataLoad.isLoadComplete) {
    return league;
  }

  //////////////////////////////////////
  // LOAD LEAGUE INFO
  //////////////////////////////////////
  if(!isLoadedLeague) {
    // If the league is currently loading return undefined
    if(isLoadingLeague) {
      return undefined;
    }

    if(league != undefined) {
      
      // Add the user's team to the loadRosterFor list if it is not already there
      // Note: There is something I noticed. If we have a value in loadRosterFor and we have loadRosterData set to true
      // then we experience some sort of weird race condition in dev mode that can cause the player screen to load then unload
      const userTeam =  league!.teams.find(leagueTeam => leagueTeam.owners?.some(owner => owner.id === dataLoad.userId));
      if (userTeam?.id && !loadRosterFor.includes(userTeam.id) && !dataLoad.loadRosterData) {
        
        loadRosterFor.push(userTeam.id);
      }

      // If we don't care about roster data return the league
      if(!dataLoad.loadRosterData && (!loadRosterFor || !loadRosterFor.length)) {
        setIsLoadedLeague(true);
        return league;
      } else if(league.teams.every(team => team.rosterLoaded) && (!dataLoad.loadContractData || league.teams.every(team => team.contractsLoaded)) && !isLoadingRoster) {
        setIsLoadedLeague(true);
        return league;
      } else if(!dataLoad.loadRosterData && league.teams.filter(team => loadRosterFor.includes(team.id!)).every(team => team.rosterLoaded && (!dataLoad.loadContractData || team.contractsLoaded))) {
        // Otherwise if the roster is loaded return the league
        setIsLoadedLeague(true);
        return league;
      } else if (isLoadingRoster) {
        return league;  
      } else if (dataLoad.loadRosterData) {
        teamApi.getTeams(dataLoad.leagueId!, dataLoad.loadContractData).then(response => {
          const newLeague = {...league};
          newLeague.teams = response.data.map(team => {
            const existingTeam = league!.teams.find(oldTeam => oldTeam.id === team.id);
            team.owners = existingTeam?.owners;
            return team;
          });
          setIsLoadingRoster(false);
          dispatch(setLeagueDataLoad(replaceLeague(newLeague, leagues)));
        });
        setIsLoadingRoster(true);
        return league;
      } else {
        loadRosterFor.filter(teamId => {
            if (!teamId) {
              return false;
            }
            const team = league!.teams.find(leagueTeam => leagueTeam.id === teamId)!;
            return !team.rosterLoaded || (dataLoad.loadContractData && !team.contractsLoaded);
          }).forEach(teamId => 
          teamApi.getTeam(teamId).then(response => {
            setNewTeam(response.data);
            setIsLoadingRoster(false);
          })
        );        
        setIsLoadingRoster(true);
        return league;
      }
    }

    // Load League Data
    loadLeagueData(dataLoad.leagueId, dataLoad.userId, leagues,
      dataLoad.loadContractData, dataLoad.loadRosterData, dispatch,
      setIsLoadingLeague, setIsLoadedLeague, dataLoad.setIsException);
    return undefined;
  }


  //////////////////////////////////////  
  // LOAD SEASON INFO
  //////////////////////////////////////
  if(!isLoadedSeason) {
    // If the season is loading, return undefined
    if(isLoadingSeason) {
      return undefined;
    } else if (league?.currentSeason) {
    // Check to see if the data is there if it is then set to loaded continue on
      setIsLoadedSeason(true);
      return league;
    } else {
      // Load Season Data
      loadSeasonData(league, leagues, dataLoad.loadBasicDraftData, dataLoad.loadBasicAuctionData, dispatch,
        setIsLoadingSeason, setIsLoadedSeason, dataLoad.setIsException);
      return undefined;
    }
  }
  
  //////////////////////////////////////
  // LOAD FULL DRAFT INFO
  //////////////////////////////////////
  if(!isLoadedFullDraft) {
    // If the season is loading, return undefined
    if(isLoadingFullDraft) {
      return undefined;
    }
    // If we are not required to load full draft, then set isLoaded to true so we skip this step
    else if (!dataLoad.loadFullDraftData) {
      setIsLoadedFullDraft(true);
      return league;
    }
    // If there is no draft scheduled, there is no more data to be pulled
    else if (!league?.currentSeason?.draft || !league?.currentSeason?.draft?.draftOrder) {
      console.log("Draft not scheduled.... skipping...");
      setIsLoadedFullDraft(true);
    } else {
      // Load Season Data
      loadFullDraftData(league, leagues, dispatch,
        setIsLoadingFullDraft, setIsLoadedFullDraft, dataLoad.setIsException);
      return undefined;
    }
  }
  
  //////////////////////////////////////
  // LOAD WAIVED CONTRACT INFO
  //////////////////////////////////////
  if(!isLoadedWaivedContracts) {
    if (isLoadingWaivedContracts) {
      return undefined;
    } else if (!dataLoad.loadWaivedContracts || league?.waivedContractsLoaded) {
      setIsLoadedWaivedContracts(true);
      return league;
    } else {
      loadWaivedContracts(league, leagues, dispatch,
        setIsLoadingWaivedContracts, setIsLoadedWaivedContracts, dataLoad.setIsException);
      return league;
    }
  }
  
  //////////////////////////////////////
  // LOAD SCHEDULE INFO
  //////////////////////////////////////
  if (!isLoadedScheduleData) {
    if (isLoadingScheduleData) {
      return undefined;
    } else if (!dataLoad.loadScheduleData) {
      setIsLoadedScheduleData(true);
    } else if (league?.currentSeason?.scheduleLoaded) {
      setIsLoadingScheduleData(false);
      setIsLoadedScheduleData(true);
      return undefined;
    } else {
      loadSchedule(league, leagues, dispatch, setIsLoadingScheduleData, setIsLoadedScheduleData, dataLoad.setIsException);
    }
  }
    
  // if Everything is loaded then return the league
  console.log("Setting League Data Load Complete");
  dataLoad.setLoadComplete(true);
  if (dataLoad.setReloading) {
    dataLoad.setReloading(false);
  }
  console.log(league);
  return league;
}

export function loadLeagueData(leagueId: String | undefined,
  userId: String | undefined,
  leagues: League[],
  loadContract: boolean,
  loadRoster: boolean,
  dispatch,
  setIsLoadingLeague : (isLoadingLeague: boolean) => void,
  setIsLoadedLeague : (isLoadedLeague: boolean) => void,
  setIsException : (isException: boolean) => void) {
    console.log("Loading League Data...");
    setIsLoadingLeague(true);

    leagueApi.getLeague(leagueId, true, userId, loadContract, loadRoster).then((resp) => {
      // Find the league, add it to the list of leagues, and update the state
      var leagueResp: League = resp.data;
      leagues = replaceLeague(leagueResp, leagues);
      dispatch(setLeagueDataLoad(leagues));
      // Set the loading flags
      setIsLoadingLeague(false);
      setIsLoadedLeague(true);
    }).catch((err) => {
      setIsException(true);
      throw err;
    });
}

export function loadSeasonData(league: League | undefined,
  leagues: League[],
  loadBasicDraftData: boolean,
  loadBasicAuctionData: boolean,
  dispatch,
  setIsLoadingSeason : (isLoadingSeason: boolean) => void,
  setIsLoadedSeason : (isLoadedSeason: boolean) => void,
  setIsException : (isException: boolean) => void) {
    console.log("Loading Season Data...");
    setIsLoadingSeason(true);
    // Note we are asking for draft data here but that does not guarantee that we will get it. If the draft has not started yet then we will not get draft data.
    leagueSeasonApi.getCurrentSeason(league?.id, loadBasicDraftData, loadBasicAuctionData).then((resp) => {
      // Update the league with the new season data
      var updatedLeague: League = {...league as League, currentSeason: resp.data};
      // Update the league in the state
      leagues = replaceLeague(updatedLeague, leagues);
      dispatch(setLeagueDataLoad(leagues));
      // Set the loading flags
      setIsLoadingSeason(false);
      setIsLoadedSeason(true);
    }).catch((err) => {
      setIsException(true);
      throw err;
    });
}




export function loadFullDraftData(league: League | undefined,
  leagues: League[],
  dispatch,
  setIsLoadingFullDraft : (isLoadingFullDraft: boolean) => void,
  setIsLoadedFullDraft : (isLoadedFullDraft: boolean) => void,
  setIsException : (isException: boolean) => void) {
    console.log("Loading Draft Data...");

    setIsLoadingFullDraft(true);

    
    leagueEventApi.getLeagueEvent(league?.currentSeason?.draft?.id).then((resp) => {
      // Update the league with the new season data
      var updatedSeason: LeagueSeason = {...league?.currentSeason as LeagueSeason, draft : resp.data};
      var updatedLeague: League = {...league as League, currentSeason: updatedSeason};
      // Update the league in the state
      leagues = replaceLeague(updatedLeague, leagues);
      dispatch(setLeagueDataLoad(leagues));
      // Set the loading flags
      setIsLoadingFullDraft(false);
      setIsLoadedFullDraft(true);
    }).catch((err) => {
      setIsException(true);
      throw err;
    });
}

export function loadWaivedContracts(league: League | undefined,
  leagues: League[],
  dispatch,
  setIsLoadingWaivedContracts : (isLoadingWaivedContracts: boolean) => void,
  setIsLoadedWaivedContracts : (isLoadedWaivedContracts: boolean) => void,
  setIsException : (isException: boolean) => void) {
    console.log("Loading Waived Contracts...");

    setIsLoadingWaivedContracts(true);

    
    contractApi.getContractsInStatus(league?.id, ContractStatus.Waived).then(response => {
      var updatedLeague: League = {...league as League, waivedContracts: response.data, waivedContractsLoaded: true};
      // Update the league in the state
      leagues = replaceLeague(updatedLeague, leagues);
      dispatch(setLeagueDataLoad(leagues));
      // Set the loading flags
      setIsLoadingWaivedContracts(false);
      setIsLoadedWaivedContracts(true);
    }).catch((err) => {
      setIsException(true);
      throw err;
    });
}

export function loadSchedule(league: League | undefined,
  leagues: League[],
  dispatch,
  setIsLoadingScheduleData : (isLoadingScheduleData: boolean) => void,
  setIsLoadedScheduleData : (isLoadedScheduleData: boolean) => void,
  setIsException : (isException: boolean) => void) {
    console.log("Loading League Schedule...");

    setIsLoadingScheduleData(true);

    
    leagueApi.getScheduleData(league?.id, league?.currentSeasonYear).then(response => {
      var updatedLeague: League = {...league as League, currentSeason: {...league!.currentSeason, schedule: response.data, scheduleLoaded: true}};
      // Update the league in the state
      leagues = replaceLeague(updatedLeague, leagues);
      dispatch(setLeagueDataLoad(leagues));
    }).catch((err) => {
      setIsException(true);
      throw err;
    });
}


// TODO: If this gets called a bunch (which it probably will) it will spam the backend with requests
// Note: This is not a hook just a function that is used to update the league data
export function useUpdateLeagueDataFromMessage(lastMessage, args, mergeOnly = false) {

  // Navigator/Dispatcher
  const dispatch = useAppDispatch();

  // State
  var leagues: League[] = useAppSelector((state) => state.leagueDataLoad);

  var leagueData: League = {teams: []};

  // Read message and update league data (or do nothing if not a league message)
  React.useEffect(() => {
    if (lastMessage) {
      const data = JSON.parse(lastMessage.data);
      if("message" in data){
        console.log("Got message: ", lastMessage.data.message);
        // May need to do processing here or maybe some decision
       } if ("league" in data) {
          console.log("Got data: ", lastMessage.data.substring(0, Math.min(30,lastMessage.data.length)))
          leagueData = data.league as League;
       } else {
          console.log("No league data found in message, skipping...");
          return;
       }
    } else {
      return;
    }
    console.log("Updating league data with new data: " + JSON.stringify(leagueData));

    var league: League | undefined = findLeague(leagueData!.id! , leagues);
    
    console.log("Updating league data for league: " + leagueData!.id);
    console.log ("Old League Data: " + JSON.stringify(league));
    console.log("New League Data: " + JSON.stringify(leagueData));

    // Merge old league data with new league data
    // More info on the spread command found here (https://www.w3schools.com/react/react_es6_spread.asp)
    league = mergeLeagueData(league!, leagueData, mergeOnly);

    dispatch(setLeagueDataLoad(replaceLeague(league, leagues)));

    console.log("Merged League Data: " + JSON.stringify(league));

  }, [lastMessage]);
}

export function useUpdateLeagueData(newLeagueData) {
  const dispatch = useAppDispatch();
  var leagues: League[] = useAppSelector((state) => state.leagueDataLoad);
  
  if (!newLeagueData) {
    return
  }
  
  dispatch(setLeagueDataLoad(replaceLeague(newLeagueData, leagues)))
}

export function useUpdateTradeDataFromMessage(lastMessage, args) {
  const dispatch = useAppDispatch();
  
  // Get trades as a plain object instead of a Map
  const trades = useAppSelector((state) => state.tradeDataLoad);
  
  React.useEffect(() => {
    if (lastMessage) {
      const data = JSON.parse(lastMessage.data);
      const tradeDataLoad = getUpdatedTradeDataLoad(data.tradeProposal, trades, args[0], data.tradeId);
      dispatch(setTradeDataLoad(tradeDataLoad));
    }
  }, [lastMessage])
}

export function useUpdatePlayerDataFromMessage(lastMessage, args) {
  const dispatch = useAppDispatch();
  
  var playerDataLoad: NflTeam[] = useAppSelector((state) => state.playerDataLoad );
  
  React.useEffect(() => {
    if (lastMessage) {
      const playerData = playerDataLoad.map(team => {
        const teamCopy = {...team};
        teamCopy.players = teamCopy.players?.map(player => {
          const playerCopy = {...player};
          playerCopy.quick_stats = {...player.quick_stats};
          return playerCopy;
        })
        return teamCopy
      });
      const playerMap = new Map(playerData.flatMap(team => team.players).map((player) => [player!.id, player]));
      const data = JSON.parse(lastMessage.data);
      Object.keys(data).forEach(playerId => {
        playerMap.get(playerId)!.quick_stats!.currentWeeksStats! = data[playerId];
      })
      dispatch(setPlayerDataLoad(playerData))
    }
  }, [lastMessage])
}

export function useUpdateNflGameDataFromMessage(lastMessage, args) {
  const dispatch = useAppDispatch();
  
  React.useEffect(() => {
    if (lastMessage) {
      const data = JSON.parse(lastMessage.data);
      dispatch(setNflGameDataLoad(data))
    }
  }, [lastMessage])
}

export function useUpdatePlayerClaimDataFromMessage(lastMessage, args) {
  const dispatch = useAppDispatch();
  
  var teamPlayerClaims: Map<string, TradeProposal[]> = useAppSelector((state) => state.playerClaimDataLoad);
  
  React.useEffect(() => {
    if (lastMessage) {
      const data = JSON.parse(lastMessage.data);
      const playerClaimDataLoad = getUpdatedPlayerClaimDataLoad(data.playerClaims, teamPlayerClaims, args[0], data.playerClaimsId);
      dispatch(playerClaimDataLoad);
    }
  }, [lastMessage])
}

export function useUpdatePlayerPoachDataFromMessage(lastMessage, args) {
  const dispatch = useAppDispatch();
  
  var teamPlayerPoachInfo: Record<string, PlayerPoachInfo[]> = useAppSelector((state) => {
    const teamPlayerPoachInfoMap = state.playerPoachDataLoad;
    // Convert Map to plain object
    const teamPlayerPoachInfoObj: Record<string, PlayerPoachInfo[]> = {};
    Object.keys(teamPlayerPoachInfoMap).forEach((key) => {
      teamPlayerPoachInfoObj[key] = teamPlayerPoachInfoMap[key];
    });
    return teamPlayerPoachInfoObj;
  });
  
  React.useEffect(() => {
    if (lastMessage) {
      const data = JSON.parse(lastMessage.data);
      const playerPoachDataLoad = getUpdatedPlayerPoachDataLoad(data.playerPoachInfo, teamPlayerPoachInfo, args[0]);
      dispatch(playerPoachDataLoad);
    }
  }, [lastMessage])
}

export function findLeague(leagueId: String, leagues: League[]) {
  var index = leagues.findIndex((league) => league.id == leagueId);
  if(index == -1) {
    return  undefined
  }
  return leagues[index];
}

export function replaceLeague(L: League, leagues: League[]) {
  
  if(!leagues.map((league) => league.id).includes(L.id)) {
    return [...leagues, L];
  }
   var updatedLeagues =leagues.map((league) => {
    if (league.id == L.id) {
      return L;
    }
    return league;
  });
  return updatedLeagues;
}

const historyKeys = new Set(['activityHistory', 'bidsForCurrentPlayer'])

// This is a function we will have to make sure it up to date and working correctly
export function mergeLeagueData(previousLeagueData: League, newLeagueData: League, mergeOnly: boolean = false) {
  if ((!newLeagueData.commissioners || !newLeagueData.commissioners![0].username) && previousLeagueData) {
    newLeagueData.commissioners = previousLeagueData.commissioners
  }
  const options = {
    customMerge: (key) => {
      if (key === 'teams') {
        return (teamArray1, teamArray2) => mergeTeamsArray(teamArray1, teamArray2, mergeOnly)
      }
      if (mergeOnly && historyKeys.has(key)) {
        return (history1, history2) => history2.length === 0 ? [] : mergeArrayOnProperty(history1, history2, "id", true, true, {})
      }
      if (mergeOnly && key === 'draftPicks') {
        return mergeDraftPicks
      }
    },
    // By default we are just going to overwrite arrays with the new data
    arrayMerge: (destinationArray, sourceArray, options) => sourceArray
  }
  return merge(previousLeagueData, newLeagueData, options);
}

function mergeDraftPicks(existingPicks, incomingPicks) {
  let newPicks = existingPicks.map(roundPicks => roundPicks.map(pick => {
    return {...pick, contract : {...pick.contract}}
  }));
  incomingPicks[0].forEach(incomingPick => {
    if (Object.keys(incomingPick).length === 2) {
      newPicks.some(roundPicks => {
        const matchingPick = roundPicks.find(pick => pick.id === incomingPick.id)
        if (matchingPick) {
          matchingPick.contract.playerId = undefined
          return true;
        }
      })
    } else {
      const matchingIndex = newPicks[incomingPick.roundNumber - 1].findIndex(pick => pick.pickNumber === incomingPick.pickNumber);
      newPicks[incomingPick.roundNumber - 1][matchingIndex] = incomingPick;
    }
  })
  return newPicks;
}

interface TradeDataLoad {
  teamId: string | undefined,
  setIsException: (isException: boolean) => void,
  isLoadComplete: boolean,
  setLoadComplete: (isLoadComplete: boolean) => void
}

export function useLoadTradeData(dataLoad: TradeDataLoad) {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  var trades = useAppSelector((state) => state.tradeDataLoad);
  
  if (isLoading || !dataLoad.teamId) {
    return [];
  }
  const teamTrades : Array<TradeProposal> | undefined = trades[dataLoad.teamId];
  if(dataLoad.isLoadComplete) {
    return teamTrades || [];
  }
  if (teamTrades) {
    dataLoad.setLoadComplete(true);
    return teamTrades;
  }
  if (isLoaded) {
    dataLoad.setLoadComplete(true);
    return teamTrades || [];
  }
  
  setIsLoading(true);
  
  tradeApi.getTradeProposalsForTeam(dataLoad.teamId).then(response => {
    const newTrades = replaceTrades(dataLoad.teamId!, response.data, trades);
    dispatch(setTradeDataLoad(newTrades));
    setIsLoading(false);
    setIsLoaded(true);
  }).catch((err) => {
    setIsLoading(false); 
    setIsLoaded(true);
    dataLoad.setIsException(true);
    throw err;
  });
  
  return teamTrades || [];
}

function replaceTrades(teamId: string, teamTrades: Array<TradeProposal>, trades: Record<string, Array<TradeProposal>>) : Record<string, TradeProposal[]> {
  const newTrades = {...trades};
  newTrades[teamId] = teamTrades;
  return newTrades;
}

export function useAddUpdateTrade(teamId: string, tradeId: string | undefined, trade: TradeProposal | null | undefined) {
  const dispatch = useAppDispatch();
  
  // In your component where you call useAddUpdateTrade
  const trades = useAppSelector((state) => {
    const tradesMap = state.tradeDataLoad;
    // Convert Map to plain object
    const tradesObj: Record<string, TradeProposal[]> = {};
    Object.entries(tradesMap).forEach((value, key) => {
      tradesObj[key] = value;
    });
    return tradesObj;
  });
  
  if (!trade) {
    return;
  }
  
  // Create a new copy of the trades object to avoid mutating the original
  const updatedTrades = { ...trades };
  
  // Get the current trades for the team or an empty array if none exist
  const teamTrades = updatedTrades[teamId] || [];
  
  // Create a new array for the team's trades to avoid reference issues
  const newTeamTrades = [...teamTrades];
  
  // Remove the trade with the given ID if it exists
  if (tradeId) {
    const tradeIndex = newTeamTrades.findIndex(t => t.id === tradeId);
    if (tradeIndex !== -1) {
      newTeamTrades.splice(tradeIndex, 1);
    }
  }
  
  // Add the new/updated trade
  newTeamTrades.push(trade);
  
  // Update the trades for the team
  updatedTrades[teamId] = newTeamTrades;
  
  // Dispatch the setTradeDataLoad action with the updated trades
  dispatch(setTradeDataLoad(updatedTrades));
}

export function useAddUpdateTrades(teamId: string, updatedTrades: TradeProposal[]) {
  const dispatch = useAppDispatch();
  
  var trades = useAppSelector((state) => state.tradeDataLoad);
  
  if (!updatedTrades.length) {
    return;
  }
  
  const newTrades = {...trades};
  const tradesForTeam = newTrades[teamId];
  let newTradesForTeam : TradeProposal[];
  if (tradesForTeam) {
    newTradesForTeam = [...tradesForTeam];
    updatedTrades.forEach(trade => {
      removeTrade(newTradesForTeam, trade.id);
      newTradesForTeam.push(trade);
    });
  } else {
    newTradesForTeam = updatedTrades;
  }
  newTrades[teamId] = newTradesForTeam;
  dispatch(setTradeDataLoad(newTrades));
}

function getUpdatedTradeDataLoad(trade: TradeProposal | null | undefined, trades: Record<string, TradeProposal[]>, teamId: string, tradeId: string | undefined): Record<string, TradeProposal[]> {
  // Create a new Map to avoid mutating the original
  const newTrades = {...trades};
  
  // Get the current trades for the team or an empty array if none exist
  const tradesForTeam = newTrades[teamId] || [];
  
  // Create a new array for the team's trades to avoid reference issues
  let newTradesForTeam = [...tradesForTeam];
  
  // Remove the trade with the given ID if it exists
  if (tradeId) {
    removeTrade(newTradesForTeam, tradeId);
  }
  
  // Add the new/updated trade if provided
  if (trade) {
    newTradesForTeam.push(trade);
  }
  
  // Update the trades for the team in the Map
  newTrades[teamId] = newTradesForTeam;
  
  return newTrades;
}

function removeTrade(newTradesForTeam, tradeId) {
   const tradeIndex = newTradesForTeam.findIndex(trade => trade.id === tradeId);
    if (~tradeIndex) {
      newTradesForTeam.splice(tradeIndex, 1);
    }
}

interface PlayerClaimDataLoad {
  teamId: string | undefined,
  setIsException: (isException: boolean) => void,
  isLoadComplete: boolean,
  setLoadComplete: (isLoadComplete: boolean) => void
}

export function useLoadPlayerClaimData(dataLoad: PlayerClaimDataLoad) {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  var playerClaims = useAppSelector((state) => state.playerClaimDataLoad);
  
  if (isLoading) {
    return [];
  }
  const teamPlayerClaims : Array<TeamPlayerClaims> | undefined = dataLoad.teamId ? playerClaims[dataLoad.teamId] : undefined;
  if(dataLoad.isLoadComplete) {
    return teamPlayerClaims || [];
  }
  if (teamPlayerClaims) {
    dataLoad.setLoadComplete(true);
    return teamPlayerClaims;
  }
  if (isLoaded) {
    dataLoad.setLoadComplete(true);
    return teamPlayerClaims || [];
  }
  
  setIsLoading(true);
  
  if (dataLoad.teamId) {
    playerClaimApi.getPlayerClaims(dataLoad.teamId).then(response => {
      const newPlayerClaims = replacePlayerClaims(dataLoad.teamId, response.data, playerClaims);
      dispatch(setPlayerClaimDataLoad(newPlayerClaims));
      setIsLoading(false);
      setIsLoaded(true);
    }).catch((err) => {
      setIsLoading(false); 
      setIsLoaded(true);
      dataLoad.setIsException(true);
      throw err;
    });
  }
  
  return teamPlayerClaims || [];
}

function replacePlayerClaims(teamId: string, teamPlayerClaims: Array<TeamPlayerClaims>, playerClaims: Record<string, Array<TeamPlayerClaims>>) : Record<string, TeamPlayerClaims[]> {
  const newPlayerClaims = {...playerClaims}
  newPlayerClaims[teamId] = teamPlayerClaims;
  return newPlayerClaims;
}

export function useAddUpdatePlayerClaim(teamId: string, teamPlayerClaimsId: string | undefined, teamPlayerClaims: TeamPlayerClaims | null | undefined) {
  const dispatch = useAppDispatch();
  
  var playerClaims = useAppSelector((state) => state.playerClaimDataLoad);
  
  if (!teamPlayerClaims) {
    return;
  }
  
  const playerClaimDataLoad = getUpdatedPlayerClaimDataLoad(teamPlayerClaims, playerClaims, teamId, teamPlayerClaimsId);
  dispatch(playerClaimDataLoad);
}

function getUpdatedPlayerClaimDataLoad(teamPlayerClaims: TeamPlayerClaims | null | undefined, playerClaims: Record<string, TeamPlayerClaims[]>, teamId: string, teamPlayerClaimsId: string | undefined) {
  const newPlayerClaims = {...playerClaims};
  const playerClaimsForTeam = newPlayerClaims[teamId];
  let newPlayerClaimsForTeam : TeamPlayerClaims[] = [];
  if (playerClaimsForTeam) {
    newPlayerClaimsForTeam = [...playerClaimsForTeam];
    removePlayerClaims(newPlayerClaimsForTeam, teamPlayerClaimsId);
  }
  if (teamPlayerClaims) {
    newPlayerClaimsForTeam.push(teamPlayerClaims);
  }
  newPlayerClaims[teamId] = newPlayerClaimsForTeam;
  return setPlayerClaimDataLoad(newPlayerClaims);
}

function getUpdatedPlayerPoachDataLoad(playerPoachInfo: PlayerPoachInfo[], teamPlayerPoachInfo: Record<string, PlayerPoachInfo[]>, teamId: string) {
  const newTeamPlayerPoachInfo = {...teamPlayerPoachInfo};
  newTeamPlayerPoachInfo[teamId] = playerPoachInfo;
  return setPlayerPoachDataLoad(newTeamPlayerPoachInfo);
}

function removePlayerClaims(newPlayerClaimsForTeam, teamPlayerClaimsId) {
   const playerClaimIndex = newPlayerClaimsForTeam.findIndex(playerClaim => playerClaim.id === teamPlayerClaimsId);
    if (~playerClaimIndex) {
      newPlayerClaimsForTeam.splice(playerClaimIndex, 1);
    }
}

function mergeTeamsArray(teamArray1, teamArray2, mergeOnly: boolean) {
  // Merge teams on ID
  let property = "id";
  const options = {
    customMerge: (key) => {
      if (key === 'owners') {
        return mergeOwnersArray
      }
      if (mergeOnly && key === 'playerRoster') {
        return mergeOnlyRoster
      }
      if (key === 'playerRoster') {
        return mergeRoster
      }
    },
    // By default we are just going to overwrite arrays with the new data
    arrayMerge: (destinationArray, sourceArray, options) => sourceArray
  }
  return mergeArrayOnProperty(teamArray1, teamArray2, property, true, true, options, true, mergeTeam);
}

function mergeTeam(oldTeam, newTeam, options) {
  const resultTeam = merge(oldTeam, newTeam, options);
  if (resultTeam.playersToRemoveFromRoster) {
      resultTeam.playersToRemoveFromRoster.forEach(playerId => 
        Object.keys(resultTeam.playerRoster).forEach((rosterPosition) => 
          resultTeam.playerRoster[rosterPosition] = resultTeam.playerRoster[rosterPosition].filter(player => !player || player.playerId !== playerId)
        )
      )
      resultTeam.playersToRemoveFromRoster = [];
    }
  return resultTeam;
}

function mergeRoster(roster1, roster2) {
  const playerOptions = {
    customMerge: (key) => {
      if (key === 'contract') {
        return mergeContract
      }
    },
    arrayMerge: (destinationArray, sourceArray, options) => sourceArray
  }
  const options = {
      customMerge: (key) => {
        if (key === 'contract') {
          return mergeContract
        }
      },
      arrayMerge: (destinationArray, sourceArray, options) => mergeArrayOnProperty(destinationArray, sourceArray, "playerId", false, true, playerOptions, false)
    }
  return merge(roster1, roster2, options);
}

function mergeOnlyRoster(roster1, roster2) {
  const options = {
    arrayMerge: (destinationArray, sourceArray, options) =>  mergeArrayOnProperty(destinationArray, sourceArray, "playerId", true, true, {})
  }
  return merge(roster1, roster2, options);
}

function mergeContract(oldContract, newContract) {
  const mergedContract = {...newContract};
  mergedContract.markedTagForNextContract = oldContract.markedTagForNextContract;
  return mergedContract;
}

function mergeOwnersArray(ownerArray1, ownerArray2) {
  let property = "id";
  return mergeArrayOnProperty(ownerArray1, ownerArray2, property, false, true);
}

/**
 * 
 * @param destArray 
 * @param sourceArray 
 * @param property 
 * @param appendDest
 * @param appendSource
 * @returns 
 */
function mergeArrayOnProperty(destArray, sourceArray, property, appendDest = true, appendSource = true, options, removeEmptyObjects = true, customMerge = merge) {
  let result = [];
  let keepNulls = !removeEmptyObjects && destArray.length === sourceArray.length;
  destArray.forEach((dest, destIdx) => {
    if (keepNulls && dest === null) {
      result.push(sourceArray[destIdx])
    }
    else if (keepNulls && sourceArray[destIdx] === null) {
      result.push(null);
    } else {
      for (let source of sourceArray) {
        if (dest && source && dest[property] === source[property]) {
          if (removeEmptyObjects && Object.keys(source).filter(key => key !== 'type').length === 1) {
            result.push(source);
          } else {
            result.push(customMerge(dest, source, options));
          }
        }
      }
    }
  });

  if (appendDest) {
    for (let dest of destArray) {
      if (dest && !result.some((obj) => obj[property] === dest[property])) {
        result.push(dest);
      }
    }
  }

  if(appendSource) {
    for (let source of sourceArray) {
      if (source && !result.some((obj) => !obj || obj[property] === source[property])) {
        result.push(source);
      }
    }
  }
  
  if (removeEmptyObjects) {
    result = result.filter(entry => !entry || Object.keys(entry).filter(key => key !== 'type').length > 1);
  }

  return result;
}
